import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ProfileModal from '../components/ProfileModal';

function Modal({ show, onClose, title, maxWidth = 'max-w-2xl', children }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl w-full ${maxWidth} flex flex-col shadow-2xl`} style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-bold">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

// Türkçe karakterleri sadeleştirip küçük harfe çevirir.
// "gul" yazınca "Gül Apartmanı" bulunsun diye.
const norm = (v) => (v || "")
  .toString()
  .toLocaleLowerCase("tr")
  .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
  .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c")
  .trim();

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Kullanıcı yönetimi
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', phone: '', password: '', role: 'apartment_manager', tenant_id: '' });
  const [savingUser, setSavingUser] = useState(false);
  const [tenantPickerSearch, setTenantPickerSearch] = useState('');
  // Kullanıcı modalı sekmeleri: 'users' | 'sites'
  const [usersTab, setUsersTab] = useState('users');
  const [siteSearch, setSiteSearch] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteOverview, setSiteOverview] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [accountUnit, setAccountUnit] = useState(null); // site sekmesinde hesap acilan daire
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [stats, setStats] = useState({ totalTenants: 0, registeredUnits: 0, totalCapacity: 0, totalInvoices: 0, totalRevenue: 0, paidInvoices: 0, revenueByMethod: {} });
  const [loading, setLoading] = useState(true);

  // Site modal
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', address: '', city: '', district: '', total_units: '', late_fee_enabled: false, late_fee_rate: 0, late_fee_cap_waived: false });

  // Daire yönetimi
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [units, setUnits] = useState([]);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({ unit_number: '', floor: '', block: '', m2_area: '', owner_name: '', owner_email: '', owner_phone: '', unit_type: '', weight_coefficient: '1.0', late_fee_exempt: false });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (!userData || userData.role !== 'super_admin') { navigate('/login'); return; }
    setUser(userData);
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [tenantsRes, unitsRes, invoicesRes] = await Promise.all([
        api.get('/tenants'),
        api.get('/units'),
        api.get('/invoices')
      ]);
      const tenantsData = tenantsRes.data.data;
      const unitsData = unitsRes.data.data;
      const invoicesData = invoicesRes.data.data;
      setTenants(tenantsData);
      const paidInvoicesList = invoicesData.filter(inv => inv.status === 'paid' && !inv.is_dues_exempt);
      const totalRevenue = paidInvoicesList.reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0);
      // Tahsilatı ödeme yöntemine göre ayır
      const revenueByMethod = { havale_eft: 0, kredi_karti: 0, nakit: 0, diger: 0 };
      paidInvoicesList.forEach(inv => {
        const amt = parseFloat(inv.total_amount) || 0;
        const m = (inv.payment_method || '').toLowerCase();
        if (m === 'iyzico' || m === 'credit_card' || m === 'kredi kartı' || m === 'kredi_karti') revenueByMethod.kredi_karti += amt;
        else if (m === 'havale_eft' || m === 'havale' || m === 'eft') revenueByMethod.havale_eft += amt;
        else if (m === 'nakit') revenueByMethod.nakit += amt;
        else revenueByMethod.diger += amt;
      });
      setStats({
        totalTenants: tenantsData.length,
        registeredUnits: unitsData.length,
        totalCapacity: tenantsData.reduce((sum, t) => sum + parseInt(t.total_units), 0),
        totalInvoices: invoicesData.length,
        totalRevenue,
        paidInvoices: paidInvoicesList.length,
        revenueByMethod
      });
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Site modal
  const openAddTenant = () => {
    setEditingTenant(null);
    setTenantForm({ name: '', address: '', city: '', district: '', total_units: '', late_fee_enabled: false, late_fee_rate: 0, late_fee_cap_waived: false });
    setShowTenantModal(true);
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({ name: tenant.name, address: tenant.address, city: tenant.city || '', district: tenant.district || '', total_units: tenant.total_units, late_fee_enabled: tenant.late_fee_enabled, late_fee_rate: tenant.late_fee_rate || 0, late_fee_cap_waived: tenant.late_fee_cap_waived || false });
    setShowTenantModal(true);
  };

  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        // Limit iptali ayrı yetkili endpointten gider; oran kontrolü backendde yapılır.
        if ((editingTenant.late_fee_cap_waived || false) !== tenantForm.late_fee_cap_waived) {
          await api.patch(`/tenants/${editingTenant.id}/cap-waiver`, { late_fee_cap_waived: tenantForm.late_fee_cap_waived });
        }
        await api.put(`/tenants/${editingTenant.id}`, tenantForm);
        alert('Site güncellendi!');
      } else {
        const created = await api.post('/tenants', tenantForm);
        if (tenantForm.late_fee_cap_waived && created?.data?.data?.id) {
          await api.patch(`/tenants/${created.data.data.id}/cap-waiver`, { late_fee_cap_waived: true });
        }
        alert('Site eklendi!');
      }
      setShowTenantModal(false);
      loadData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'İşlem başarısız'));
    }
  };

  const handleDeleteTenant = async (id, name) => {
    if (!window.confirm(`"${name}" sitesini silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/tenants/${id}`);
      loadData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'Silme başarısız'));
    }
  };

  // Daire yönetimi
  const openUnitsModal = async (tenant) => {
    setSelectedTenant(tenant);
    setShowUnitForm(false);
    setEditingUnit(null);
    try {
      const res = await api.get(`/units?tenant_id=${tenant.id}`);
      setUnits(res.data.data);
    } catch {}
    setShowUnitsModal(true);
  };

  const openAddUnit = () => {
    setEditingUnit(null);
    setUnitForm({ unit_number: '', floor: '', block: '', m2_area: '', owner_name: '', owner_email: '', owner_phone: '', unit_type: '', weight_coefficient: '1.0', late_fee_exempt: false });
    setShowUnitForm(true);
  };

  const openEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({
      unit_number: unit.unit_number,
      floor: unit.floor || '',
      block: unit.block || '',
      m2_area: unit.m2_area || '',
      owner_name: unit.owner_name,
      owner_email: unit.owner_email || '',
      owner_phone: unit.owner_phone || '',
      weight_coefficient: unit.weight_coefficient || '1.0',
      unit_type: unit.unit_type || '',
      late_fee_exempt: unit.late_fee_exempt || false,
      dues_exempt: unit.dues_exempt || false,
      dues_exempt_reason: unit.dues_exempt_reason || ''
    });
    setShowUnitForm(true);
  };

  const handleUnitTypeSelect = (typeName) => {
    const types = typeof selectedTenant?.unit_types === "string"
      ? JSON.parse(selectedTenant.unit_types)
      : (selectedTenant?.unit_types || []);
    const found = types.find(t => t.name === typeName);
    setUnitForm(prev => ({
      ...prev,
      unit_type: typeName,
      weight_coefficient: found ? String(found.coefficient) : prev.weight_coefficient
    }));
  };

  const handleUnitSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUnit) {
        await api.put(`/units/${editingUnit.id}`, unitForm);
      } else {
        await api.post('/units', { ...unitForm, tenant_id: selectedTenant.id });
      }
      const res = await api.get(`/units?tenant_id=${selectedTenant.id}`);
      setUnits(res.data.data);
      setShowUnitForm(false);
      setEditingUnit(null);
      loadData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'İşlem başarısız'));
    }
  };

  const handleDeleteUnit = async (id, number) => {
    if (!window.confirm(`Daire ${number} silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/units/${id}`);
      setUnits(prev => prev.filter(u => u.id !== id));
      loadData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'Silme başarısız'));
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate('/login'); };

  const openUsersModal = async () => {
    setUsersLoading(true);
    setResetUserId(null); setNewPassword(''); setResetMessage(null);
    setUsersTab('users'); setSelectedSite(null); setSiteOverview(null); setSiteSearch('');
    setShowUsersModal(true);
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data.data);
    } catch (e) { console.error('Kullanıcı listesi hatası:', e); }
    finally { setUsersLoading(false); }
  };

  const openSiteOverview = async (tenant) => {
    setSelectedSite(tenant);
    setSiteOverview(null);
    setSiteLoading(true);
    setResetMessage(null);
    setResetUserId(null);
    try {
      const res = await api.get(`/tenants/${tenant.id}/overview`);
      setSiteOverview(res.data.data);
    } catch (e) {
      setResetMessage({ type: 'error', text: e.response?.data?.message || 'Site bilgileri alınamadı' });
    } finally { setSiteLoading(false); }
  };

  const refreshSiteOverview = async () => {
    if (selectedSite) {
      try {
        const res = await api.get(`/tenants/${selectedSite.id}/overview`);
        setSiteOverview(res.data.data);
      } catch (e) { /* sessiz */ }
    }
  };

  const openUnitAccount = (unit) => {
    setAccountUnit(unit);
    setResetUserId(null);
    setResetMessage(null);
    setNewUser({
      full_name: unit.owner_name || '',
      email: '',
      phone: unit.owner_phone || '',
      password: '',
      role: 'resident',
      tenant_id: selectedSite?.id || '',
    });
  };

  const handleCreateUnitAccount = async () => {
    if (!newUser.full_name.trim() || !newUser.email.trim()) {
      setResetMessage({ type: 'error', text: 'Ad soyad ve e-posta zorunlu' });
      return;
    }
    if (!newUser.password || newUser.password.length < 8) {
      setResetMessage({ type: 'error', text: 'Şifre en az 8 karakter olmalı' });
      return;
    }
    setSavingUser(true);
    try {
      await api.post('/auth/register', {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim(),
        phone: newUser.phone.trim() || null,
        password: newUser.password,
        role: 'resident',
        tenant_id: selectedSite.id,
        unit_id: accountUnit.id,
      });
      setResetMessage({ type: 'success', text: `Daire ${accountUnit.unit_number} için hesap oluşturuldu` });
      setAccountUnit(null);
      await refreshSiteOverview();
    } catch (e) {
      setResetMessage({ type: 'error', text: e.response?.data?.message || 'Hesap oluşturulamadı' });
    } finally { setSavingUser(false); }
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name.trim() || !newUser.email.trim()) {
      setResetMessage({ type: 'error', text: 'Ad soyad ve e-posta zorunlu' });
      return;
    }
    if (!newUser.password || newUser.password.length < 8) {
      setResetMessage({ type: 'error', text: 'Şifre en az 8 karakter olmalı' });
      return;
    }
    if (newUser.role === 'apartment_manager' && !newUser.tenant_id) {
      setResetMessage({ type: 'error', text: 'Yönetici için site seçmelisiniz' });
      return;
    }
    setSavingUser(true);
    try {
      await api.post('/auth/register', {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim(),
        phone: newUser.phone.trim() || null,
        password: newUser.password,
        role: newUser.role,
        tenant_id: newUser.tenant_id || null,
      });
      setResetMessage({ type: 'success', text: `${newUser.full_name} hesabı oluşturuldu` });
      setNewUser({ full_name: '', email: '', phone: '', password: '', role: 'apartment_manager', tenant_id: '' });
      setShowAddUser(false);
      const res = await api.get('/auth/users');
      setUsers(res.data.data);
    } catch (e) {
      setResetMessage({ type: 'error', text: e.response?.data?.message || 'Hesap oluşturulamadı' });
    } finally { setSavingUser(false); }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      setResetMessage({ type: 'error', text: 'Şifre en az 8 karakter olmalı' });
      return;
    }
    try {
      const res = await api.patch(`/auth/users/${userId}/reset-password`, { new_password: newPassword });
      setResetMessage({ type: 'success', text: res.data.message });
      if (usersTab === 'sites') refreshSiteOverview();
      setResetUserId(null); setNewPassword('');
    } catch (e) {
      setResetMessage({ type: 'error', text: e.response?.data?.message || 'Hata oluştu' });
    }
  };

  // Site arama + şehir filtresi (client-side: liste zaten tamamen yüklü)
  const cityOptions = [...new Set(tenants.map(t => t.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const filteredTenants = tenants.filter(t => {
    if (cityFilter && t.city !== cityFilter) return false;
    if (!tenantSearch.trim()) return true;
    const q = norm(tenantSearch);
    return [t.name, t.address, t.city, t.district].some(f => norm(f).includes(q));
  });

  // Kullanıcı arama + rol filtresi
  const filteredUsers = users.filter(u => {
    if (userRoleFilter && u.role !== userRoleFilter) return false;
    if (!userSearch.trim()) return true;
    const q = norm(userSearch);
    return [u.full_name, u.email, u.tenant_name, u.unit_number].some(f => norm(f).includes(q));
  });
  const roleCounts = {
    super_admin: users.filter(u => u.role === 'super_admin').length,
    apartment_manager: users.filter(u => u.role === 'apartment_manager').length,
    resident: users.filter(u => u.role === 'resident').length,
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Yükleniyor...</div></div>;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Super Admin Panel</h1>
            <p className="text-purple-100 text-sm mt-1">Sistem Yönetimi</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white font-semibold cursor-pointer hover:underline" onClick={() => setShowProfileModal(true)}>{user?.full_name}</span>
            <button onClick={openUsersModal} className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-sm font-semibold px-3 py-2 rounded-lg">👥 Kullanıcılar</button>
            <button onClick={handleLogout} className="bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-purple-50">Çıkış</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* İstatistikler */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Toplam Site', value: stats.totalTenants, color: 'text-purple-600' },
            { label: 'Kayıtlı Daire', value: stats.registeredUnits, color: 'text-blue-600' },
            { label: 'Toplam Kapasite', value: stats.totalCapacity, color: 'text-indigo-600' },
            { label: 'Toplam Fatura', value: stats.totalInvoices, color: 'text-orange-600' },
            { label: 'Ödenen Fatura', value: stats.paidInvoices, color: 'text-green-600' },
            { label: 'Toplam Tahsilat', value: `₺${stats.totalRevenue.toFixed(0)}`, color: 'text-green-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-gray-500 text-xs mb-1">{s.label}</h3>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tahsilat kırılımı */}
        <div className="bg-white rounded-lg shadow p-5 mb-8">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-semibold text-gray-800">💰 Tahsilat Dağılımı</h3>
            <span className="text-sm text-gray-500">Toplam <strong className="text-green-600">₺{stats.totalRevenue.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</strong></span>
          </div>
          {stats.totalRevenue > 0 ? (
            <>
              <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-4">
                {[
                  { key: 'kredi_karti', color: 'bg-blue-500' },
                  { key: 'havale_eft', color: 'bg-purple-500' },
                  { key: 'nakit', color: 'bg-green-500' },
                  { key: 'diger', color: 'bg-gray-400' },
                ].map(b => {
                  const v = stats.revenueByMethod?.[b.key] || 0;
                  const pct = (v / stats.totalRevenue) * 100;
                  return pct > 0 ? <div key={b.key} className={b.color} style={{ width: `${pct}%` }} title={`%${pct.toFixed(1)}`} /> : null;
                })}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'kredi_karti', label: 'Kredi Kartı (Online)', dot: 'bg-blue-500' },
                  { key: 'havale_eft', label: 'Havale / EFT', dot: 'bg-purple-500' },
                  { key: 'nakit', label: 'Nakit (Elden)', dot: 'bg-green-500' },
                  { key: 'diger', label: 'Diğer', dot: 'bg-gray-400' },
                ].map(m => {
                  const v = stats.revenueByMethod?.[m.key] || 0;
                  const pct = stats.totalRevenue > 0 ? (v / stats.totalRevenue) * 100 : 0;
                  return (
                    <div key={m.key}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
                        <span className="text-xs text-gray-500">{m.label}</span>
                      </div>
                      <p className="text-lg font-bold text-gray-800">₺{v.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</p>
                      <p className="text-xs text-gray-400">%{pct.toFixed(1)}</p>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">Henüz tahsilat yok.</p>
          )}
        </div>

        <button onClick={openAddTenant} className="mb-6 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 shadow">
          + Yeni Site Ekle
        </button>

        {/* Siteler Listesi */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-xl font-semibold">
                Siteler
                {(tenantSearch || cityFilter) && (
                  <span className="ml-2 text-sm font-normal text-gray-500">{filteredTenants.length} / {tenants.length}</span>
                )}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                  <input
                    type="text"
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    placeholder="Site, adres veya ilçe ara..."
                    className="w-64 pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  {tenantSearch && (
                    <button onClick={() => setTenantSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
                  )}
                </div>
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="py-2 px-3 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Tüm iller</option>
                  {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Konum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kapasite</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gecikme Faizi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">{tenant.name}</td>
                  <td className="px-6 py-4 text-sm max-w-xs">
                    {(tenant.city || tenant.district)
                      ? <span className="text-gray-800 font-medium">{[tenant.city, tenant.district].filter(Boolean).join(" / ")}</span>
                      : <span className="text-gray-300 text-xs">Konum girilmemiş</span>}
                    <div className="text-xs text-gray-400 truncate">{tenant.address}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{tenant.total_units} daire</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.late_fee_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {tenant.late_fee_enabled ? `Aktif %${tenant.late_fee_rate}${tenant.late_fee_cap_waived ? ' ⚠️ limitsiz' : ''}` : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {tenant.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button onClick={() => openUnitsModal(tenant)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">🏠 Daireler</button>
                      <button onClick={() => openEditTenant(tenant)} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">Düzenle</button>
                      <button onClick={() => handleDeleteTenant(tenant.id, tenant.name)} className="text-red-600 hover:text-red-800 text-sm font-medium">Sil</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTenants.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-400 text-sm">
                    {tenants.length === 0 ? "Henüz site eklenmemiş." : "Aramanızla eşleşen site bulunamadı."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SİTE EKLE/DÜZENLE MODAL */}
      <Modal show={showTenantModal} onClose={() => setShowTenantModal(false)} title={editingTenant ? 'Site Düzenle' : 'Yeni Site Ekle'}>
        <form onSubmit={handleTenantSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Site Adı</label>
            <input type="text" value={tenantForm.name} onChange={(e) => setTenantForm({...tenantForm, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Adres</label>
            <textarea value={tenantForm.address} onChange={(e) => setTenantForm({...tenantForm, address: e.target.value})} className="w-full px-4 py-2 border rounded-lg" rows="2" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">İl</label>
              <input type="text" value={tenantForm.city} onChange={(e) => setTenantForm({...tenantForm, city: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="ör: İstanbul" list="city-list" />
              <datalist id="city-list">
                {[...new Set(tenants.map(t => t.city).filter(Boolean))].sort().map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">İlçe</label>
              <input type="text" value={tenantForm.district} onChange={(e) => setTenantForm({...tenantForm, district: e.target.value})} className="w-full px-4 py-2 border rounded-lg" placeholder="ör: Kadıköy" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Daire Sayısı (Kapasite)</label>
            <input type="number" value={tenantForm.total_units} onChange={(e) => setTenantForm({...tenantForm, total_units: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required min="1" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input type="checkbox" id="late_fee_enabled" checked={tenantForm.late_fee_enabled} onChange={(e) => setTenantForm({...tenantForm, late_fee_enabled: e.target.checked})} className="w-5 h-5" />
            <label htmlFor="late_fee_enabled" className="text-sm font-medium cursor-pointer">Gecikme Faizi Aktif</label>
          </div>
          {tenantForm.late_fee_enabled && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Aylık Gecikme Faiz Oranı (%)</label>
                <input type="number" value={tenantForm.late_fee_rate} onChange={(e) => setTenantForm({...tenantForm, late_fee_rate: e.target.value})} className="w-full px-4 py-2 border rounded-lg" step="0.1" min="0" max={tenantForm.late_fee_cap_waived ? 100 : 5} />
                <p className="text-xs text-gray-500 mt-1">Basit usul. Yönetici bu oranı kendi panelinden de değiştirebilir.</p>
              </div>
              <div className={`p-3 rounded-lg border ${tenantForm.late_fee_cap_waived ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="cap_waived" checked={tenantForm.late_fee_cap_waived} onChange={(e) => setTenantForm({...tenantForm, late_fee_cap_waived: e.target.checked})} className="w-5 h-5" />
                  <label htmlFor="cap_waived" className="text-sm font-medium cursor-pointer">Yasal limit iptali (%5 sınırını kaldır)</label>
                </div>
                {tenantForm.late_fee_cap_waived ? (
                  <p className="text-xs text-red-700 mt-2">
                    ⚠️ Bu site %5 üstü oran girebilir. Yönetici, oranı değiştirmeden önce kendi panelinde yasal sorumluluk metnini onaylamak zorundadır.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">Yönetici en fazla %5 girebilir (KMK md. 20).</p>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowTenantModal(false)} className="px-6 py-2 border rounded-lg hover:bg-gray-50">İptal</button>
            <button type="submit" className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">{editingTenant ? 'Güncelle' : 'Ekle'}</button>
          </div>
        </form>
      </Modal>

      {/* DAİRE YÖNETİMİ MODAL */}
      <Modal show={showUnitsModal} onClose={() => { setShowUnitsModal(false); setShowUnitForm(false); }} title={`🏠 ${selectedTenant?.name} — Daireler`} maxWidth="max-w-4xl">
        {!showUnitForm ? (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">{units.length} daire kayıtlı</p>
              <button onClick={openAddUnit} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">+ Daire Ekle</button>
            </div>
            {units.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">Henüz daire eklenmemiş.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">No</th>
                    <th className="px-3 py-2 text-left">Kat/Blok</th>
                    <th className="px-3 py-2 text-left">Mal Sahibi</th>
                    <th className="px-3 py-2 text-left">Telefon</th>
                    <th className="px-3 py-2 text-left">Tip</th>
                    <th className="px-3 py-2 text-left">Katsayı</th>
                    <th className="px-3 py-2 text-left">Muafiyet</th>
                    <th className="px-3 py-2 text-left">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((unit, idx) => (
                    <tr key={unit.id} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-3 py-2 font-semibold">{unit.unit_number}</td>
                      <td className="px-3 py-2 text-gray-500">{unit.floor ? `Kat ${unit.floor}` : '—'}{unit.block ? ` / ${unit.block}` : ''}</td>
                      <td className="px-3 py-2">{unit.owner_name}</td>
                      <td className="px-3 py-2 text-gray-500">{unit.owner_phone || '—'}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{unit.unit_type || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${parseFloat(unit.weight_coefficient) !== 1.0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                          ×{parseFloat(unit.weight_coefficient).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1 items-start">
                          {unit.dues_exempt && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full" title={unit.dues_exempt_reason || ''}>🛡️ Aidat muaf</span>}
                          {unit.late_fee_exempt && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Faiz muaf</span>}
                          {!unit.dues_exempt && !unit.late_fee_exempt && <span className="text-gray-300 text-xs">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button onClick={() => openEditUnit(unit)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Düzenle</button>
                          <button onClick={() => handleDeleteUnit(unit.id, unit.unit_number)} className="text-red-500 hover:text-red-700 text-xs font-medium">Sil</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : (
          <form onSubmit={handleUnitSubmit} className="space-y-4">
            <h3 className="font-semibold text-gray-800 mb-2">{editingUnit ? `Daire ${editingUnit.unit_number} Düzenle` : 'Yeni Daire Ekle'}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Daire No *</label>
                <input type="text" value={unitForm.unit_number} onChange={(e) => setUnitForm({...unitForm, unit_number: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" required />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Kat</label>
                <input type="number" value={unitForm.floor} onChange={(e) => setUnitForm({...unitForm, floor: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Blok</label>
                <input type="text" value={unitForm.block} onChange={(e) => setUnitForm({...unitForm, block: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" placeholder="A, B..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Mal Sahibi Adı *</label>
              <input type="text" value={unitForm.owner_name} onChange={(e) => setUnitForm({...unitForm, owner_name: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">E-posta</label>
                <input type="email" value={unitForm.owner_email} onChange={(e) => setUnitForm({...unitForm, owner_email: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Telefon</label>
                <input type="text" value={unitForm.owner_phone} onChange={(e) => setUnitForm({...unitForm, owner_phone: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">m² Alan</label>
                <input type="number" value={unitForm.m2_area} onChange={(e) => setUnitForm({...unitForm, m2_area: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
            {/* Daire tipi + katsayı */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Daire Tipi</label>
                {(() => {
                  const types = typeof selectedTenant?.unit_types === "string"
                    ? JSON.parse(selectedTenant.unit_types)
                    : (selectedTenant?.unit_types || []);
                  return types.length > 0 ? (
                    <select value={unitForm.unit_type} onChange={(e) => handleUnitTypeSelect(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                      <option value="">— Manuel gir —</option>
                      {types.map((t, i) => (
                        <option key={i} value={t.name}>{t.name} (×{Number(t.coefficient).toFixed(2)})</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={unitForm.unit_type} onChange={(e) => setUnitForm({...unitForm, unit_type: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" placeholder="ör: 3+1, Dükkan..." />
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">
                  Aidat Katsayısı
                  <span className="text-gray-400 ml-1 font-normal">(1.0 = normal)</span>
                </label>
                <input type="number" value={unitForm.weight_coefficient} onChange={(e) => setUnitForm({...unitForm, weight_coefficient: e.target.value})} className="w-full border rounded px-3 py-2 text-sm" step="0.05" min="0.1" />
                {unitForm.unit_type && (typeof selectedTenant?.unit_types === "string" ? JSON.parse(selectedTenant.unit_types) : (selectedTenant?.unit_types || [])).find(t => t.name === unitForm.unit_type) && (
                  <span className="text-xs text-blue-600 mt-0.5 block">← {unitForm.unit_type} tipinden otomatik</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
              <input type="checkbox" id="unit_exempt" checked={unitForm.late_fee_exempt} onChange={(e) => setUnitForm({...unitForm, late_fee_exempt: e.target.checked})} className="w-4 h-4" />
              <label htmlFor="unit_exempt" className="text-sm cursor-pointer"><strong>Faiz muafiyeti</strong> — aidat borcu var, gecikme faizi işlemez</label>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="unit_dues_exempt" checked={unitForm.dues_exempt} onChange={(e) => setUnitForm({...unitForm, dues_exempt: e.target.checked})} className="w-4 h-4" />
                <label htmlFor="unit_dues_exempt" className="text-sm cursor-pointer"><strong>Aidat muafiyeti</strong> — hiç aidat ödemez (kapıcı vb.)</label>
              </div>
              {unitForm.dues_exempt && (
                <div>
                  <input type="text" value={unitForm.dues_exempt_reason} onChange={(e) => setUnitForm({...unitForm, dues_exempt_reason: e.target.value})} placeholder="Gerekçe (zorunlu) — ör: Kapıcı dairesi, 2025 GK kararı" className="w-full border rounded px-3 py-2 text-sm" required />
                  <p className="text-xs text-gray-500 mt-1">Bu daireye bundan sonra ₺0 fatura kesilir, borcu oluşmaz.</p>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => { setShowUnitForm(false); setEditingUnit(null); }} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">İptal</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{editingUnit ? 'Güncelle' : 'Ekle'}</button>
            </div>
          </form>
        )}
      </Modal>
      <ProfileModal
        show={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        onUpdate={(updatedUser) => setUser(prev => ({ ...prev, ...updatedUser }))}
      />

      {/* KULLANICILAR MODAL */}
      <Modal show={showUsersModal} onClose={() => setShowUsersModal(false)} title="👥 Kullanıcı Yönetimi" maxWidth="max-w-3xl">
        {resetMessage && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${resetMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {resetMessage.type === 'success' ? '✅ ' : '❌ '}{resetMessage.text}
          </div>
        )}

        {/* Sekmeler */}
        <div className="flex gap-1 mb-4 border-b">
          {[
            { key: 'users', label: '👤 Kullanıcılar' },
            { key: 'sites', label: '🏢 Siteler' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setUsersTab(t.key); setResetMessage(null); setResetUserId(null); setShowAddUser(false); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${usersTab === t.key ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {usersTab === 'users' && (<>
        {/* Yeni kullanıcı */}
        {!showAddUser ? (
          <button onClick={() => { setShowAddUser(true); setResetMessage(null); }} className="w-full mb-4 border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 text-gray-600 hover:text-purple-600 text-sm font-medium py-2.5 rounded-xl">
            + Yeni Kullanıcı Oluştur
          </button>
        ) : (
          <div className="mb-4 border rounded-xl p-4 bg-purple-50 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-gray-800">Yeni Kullanıcı</h4>
              <button onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input type="text" value={newUser.full_name} onChange={(e) => setNewUser({...newUser, full_name: e.target.value})} placeholder="Ad Soyad *" className="border rounded-lg px-3 py-2 text-sm" />
              <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="E-posta *" className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} placeholder="Telefon" className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Şifre (en az 8 karakter) *" className="border rounded-lg px-3 py-2 text-sm" />
              <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="apartment_manager">👔 Site Yöneticisi</option>
                <option value="resident">🏠 Daire Sakini</option>
                <option value="super_admin">⚡ Süper Admin</option>
              </select>
              <div className="sm:col-span-2">
                {newUser.role === 'super_admin' ? (
                  <div className="border rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-400">Site gerekmez</div>
                ) : newUser.tenant_id ? (
                  <div className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm bg-white">
                    <span className="font-medium text-gray-800 truncate">
                      {tenants.find(t => t.id === newUser.tenant_id)?.name}
                      {tenants.find(t => t.id === newUser.tenant_id)?.city && (
                        <span className="text-gray-400 font-normal"> — {tenants.find(t => t.id === newUser.tenant_id).city}</span>
                      )}
                    </span>
                    <button onClick={() => { setNewUser({...newUser, tenant_id: ''}); setTenantPickerSearch(''); }} className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-2">×</button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={tenantPickerSearch}
                      onChange={(e) => setTenantPickerSearch(e.target.value)}
                      placeholder="🔍 Site ara ve seç..."
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                    <div className="mt-1 border rounded-lg bg-white max-h-36 overflow-y-auto divide-y">
                      {tenants
                        .filter(t => !tenantPickerSearch.trim() || [t.name, t.city, t.district].some(f => norm(f).includes(norm(tenantPickerSearch))))
                        .slice(0, 50)
                        .map(t => (
                          <button
                            key={t.id}
                            onClick={() => { setNewUser({...newUser, tenant_id: t.id}); setTenantPickerSearch(''); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50"
                          >
                            <span className="font-medium text-gray-800">{t.name}</span>
                            {(t.city || t.district) && (
                              <span className="text-xs text-gray-400 ml-2">{[t.city, t.district].filter(Boolean).join(' / ')}</span>
                            )}
                          </button>
                        ))}
                      {tenants.filter(t => !tenantPickerSearch.trim() || [t.name, t.city, t.district].some(f => norm(f).includes(norm(tenantPickerSearch)))).length === 0 && (
                        <p className="px-3 py-3 text-xs text-gray-400 text-center">Eşleşen site yok</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button onClick={handleCreateUser} disabled={savingUser} className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg">
              {savingUser ? 'Oluşturuluyor...' : 'Hesabı Oluştur'}
            </button>
            <p className="text-xs text-gray-500">Şifreyi kullanıcıya kendiniz iletmelisiniz.</p>
          </div>
        )}

        {/* Arama + rol filtresi */}
        <div className="mb-4 space-y-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input
              type="text"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="İsim, e-posta veya site ara..."
              className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            {userSearch && (
              <button onClick={() => setUserSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: '', label: `Tümü (${users.length})` },
              { key: 'apartment_manager', label: `👔 Yöneticiler (${roleCounts.apartment_manager})` },
              { key: 'resident', label: `🏠 Sakinler (${roleCounts.resident})` },
              { key: 'super_admin', label: `⚡ Süper Admin (${roleCounts.super_admin})` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setUserRoleFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${userRoleFilter === f.key ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {usersLoading ? (
          <p className="text-gray-400 text-sm">Yükleniyor...</p>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {filteredUsers.map(u => (
              <div key={u.id} className="border rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">{u.full_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        u.role === 'apartment_manager' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {u.role === 'super_admin' ? 'Süper Admin' : u.role === 'apartment_manager' ? 'Yönetici' : 'Sakin'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{u.email}</p>
                    {u.tenant_name && <p className="text-xs text-gray-400">{u.tenant_name}{u.unit_number ? ` — Daire ${u.unit_number}` : ''}</p>}
                  </div>
                  <button
                    onClick={() => { setResetUserId(u.id); setNewPassword(''); setResetMessage(null); }}
                    className="text-sm bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg font-medium"
                  >
                    🔑 Şifre Sıfırla
                  </button>
                </div>
                {resetUserId === u.id && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Yeni şifre (en az 8 karakter)"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400"
                    />
                    <button
                      onClick={() => handleResetPassword(u.id)}
                      className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                    >
                      Kaydet
                    </button>
                    <button
                      onClick={() => { setResetUserId(null); setNewPassword(''); }}
                      className="border text-gray-500 hover:bg-gray-50 text-sm px-3 py-2 rounded-lg"
                    >
                      İptal
                    </button>
                  </div>
                )}
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-gray-400 text-sm text-center py-6">
                {users.length === 0 ? 'Kullanıcı bulunamadı.' : 'Aramanızla eşleşen kullanıcı yok.'}
              </p>
            )}
          </div>
        )}
        </>)}

        {/* ─── SİTELER SEKMESİ ─── */}
        {usersTab === 'sites' && (
          <div>
            {!selectedSite ? (
              <>
                <div className="relative mb-4">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                  <input
                    type="text"
                    value={siteSearch}
                    onChange={e => setSiteSearch(e.target.value)}
                    placeholder="Site adı, il veya ilçe ara..."
                    className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  {siteSearch && (
                    <button onClick={() => setSiteSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
                  )}
                </div>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                  {tenants
                    .filter(t => !siteSearch.trim() || [t.name, t.city, t.district, t.address].some(f => norm(f).includes(norm(siteSearch))))
                    .map(t => (
                      <button key={t.id} onClick={() => openSiteOverview(t)} className="w-full text-left border rounded-xl p-4 hover:border-purple-400 hover:bg-purple-50">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800">{t.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {[t.city, t.district].filter(Boolean).join(' / ') || 'Konum girilmemiş'}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-semibold text-purple-600">{t.total_units} daire</p>
                            <p className="text-xs text-gray-400">Detay →</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  {tenants.filter(t => !siteSearch.trim() || [t.name, t.city, t.district, t.address].some(f => norm(f).includes(norm(siteSearch)))).length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-6">Eşleşen site bulunamadı.</p>
                  )}
                </div>
              </>
            ) : (
              <div>
                <button onClick={() => { setSelectedSite(null); setSiteOverview(null); setResetUserId(null); setAccountUnit(null); }} className="text-sm text-purple-600 hover:underline mb-3">← Site listesine dön</button>

                {siteLoading ? (
                  <p className="text-gray-400 text-sm py-6 text-center">Yükleniyor...</p>
                ) : siteOverview ? (
                  <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-4">
                    {/* Site künyesi */}
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-4">
                      <h3 className="font-bold text-lg text-gray-800">{siteOverview.tenant.name}</h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {[siteOverview.tenant.city, siteOverview.tenant.district].filter(Boolean).join(' / ') || 'Konum girilmemiş'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{siteOverview.tenant.address}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                        {[
                          { label: 'Kayıtlı Daire', value: siteOverview.summary.total_units, sub: `Kapasite: ${siteOverview.summary.declared_capacity}` },
                          { label: 'Hesabı Olan', value: siteOverview.summary.with_account, sub: `${siteOverview.summary.without_account} eksik`, color: 'text-green-600' },
                          { label: 'Tahsil Edilen', value: `₺${Number(siteOverview.summary.collected).toLocaleString('tr-TR')}`, color: 'text-green-600' },
                          { label: 'Bekleyen Borç', value: `₺${Number(siteOverview.summary.outstanding).toLocaleString('tr-TR')}`, color: 'text-red-600' },
                        ].map((k, i) => (
                          <div key={i} className="bg-white rounded-lg p-2.5">
                            <p className="text-[11px] text-gray-400">{k.label}</p>
                            <p className={`text-base font-bold ${k.color || 'text-gray-800'}`}>{k.value}</p>
                            {k.sub && <p className="text-[10px] text-gray-400">{k.sub}</p>}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-3 mt-3 text-xs text-gray-500 flex-wrap">
                        <span>Faiz: {siteOverview.tenant.late_fee_enabled ? `%${siteOverview.tenant.late_fee_rate} (basit usul)` : 'Pasif'}</span>
                        {siteOverview.tenant.late_fee_cap_waived && <span className="text-red-600 font-medium">⚠️ Limit iptali tanımlı</span>}
                        {siteOverview.summary.exempt_units > 0 && <span>🛡️ {siteOverview.summary.exempt_units} muaf daire</span>}
                      </div>
                    </div>

                    {/* Yöneticiler */}
                    <div>
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">👔 Site Yöneticisi</h4>
                      {siteOverview.managers.length === 0 ? (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠️ Bu siteye henüz yönetici atanmamış.</p>
                      ) : siteOverview.managers.map(m => (
                        <div key={m.id} className="border rounded-xl p-3 mb-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-gray-800">{m.full_name}</p>
                              <p className="text-xs text-gray-500 truncate">{m.email}</p>
                              {m.phone && <p className="text-xs text-gray-400">{m.phone}</p>}
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {m.last_login ? `Son giriş: ${new Date(m.last_login).toLocaleDateString('tr-TR')}` : 'Henüz giriş yapmamış'}
                              </p>
                            </div>
                            <button onClick={() => { setResetUserId(resetUserId === m.id ? null : m.id); setNewPassword(''); setResetMessage(null); }} className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap">🔑 Şifre</button>
                          </div>
                          {resetUserId === m.id && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Yeni şifre (min 6)" className="flex-1 min-w-[160px] border rounded-lg px-3 py-1.5 text-sm" />
                              <button onClick={() => handleResetPassword(m.id)} className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-3 py-1.5 rounded-lg font-semibold">Kaydet</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Daireler ve sakinler */}
                    <div>
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">
                        🏠 Daireler ve Sakinler
                        <span className="font-normal text-gray-400 ml-1">({siteOverview.summary.with_account}/{siteOverview.summary.total_units} hesap açık)</span>
                      </h4>
                      {siteOverview.units.length === 0 ? (
                        <p className="text-sm text-gray-400 py-3">Bu sitede kayıtlı daire yok.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {siteOverview.units.map(u => (
                            <div key={u.id} className={`border rounded-xl p-3 ${!u.user_id ? 'bg-amber-50 border-amber-200' : ''}`}>
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Daire {u.unit_number}</span>
                                    <span className="text-sm font-medium text-gray-800">{u.owner_name || '—'}</span>
                                    {u.dues_exempt && <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">🛡️ Muaf</span>}
                                  </div>
                                  {u.user_id ? (
                                    <>
                                      <p className="text-xs text-gray-500 mt-0.5 truncate">✅ {u.user_email}</p>
                                      <p className="text-[11px] text-gray-400">
                                        {u.last_login ? `Son giriş: ${new Date(u.last_login).toLocaleDateString('tr-TR')}` : 'Henüz giriş yapmamış'}
                                      </p>
                                    </>
                                  ) : (
                                    <p className="text-xs text-amber-700 mt-0.5">⚠️ Hesap açılmamış — sakin panele giremez</p>
                                  )}
                                </div>
                                {u.user_id ? (
                                  <button onClick={() => { setResetUserId(resetUserId === u.user_id ? null : u.user_id); setNewPassword(''); setResetMessage(null); setAccountUnit(null); }} className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap">🔑 Şifre</button>
                                ) : (
                                  <button onClick={() => accountUnit?.id === u.id ? setAccountUnit(null) : openUnitAccount(u)} className="text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap">+ Hesap Aç</button>
                                )}
                              </div>
                              {!u.user_id && accountUnit?.id === u.id && (
                                <div className="mt-3 bg-white border rounded-lg p-3 space-y-2">
                                  <p className="text-xs font-semibold text-gray-700">Daire {u.unit_number} için sakin hesabı</p>
                                  <div className="grid sm:grid-cols-2 gap-2">
                                    <input type="text" value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})} placeholder="Ad Soyad *" className="border rounded-lg px-3 py-1.5 text-sm" />
                                    <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} placeholder="E-posta *" className="border rounded-lg px-3 py-1.5 text-sm" />
                                    <input type="text" value={newUser.phone} onChange={e => setNewUser({...newUser, phone: e.target.value})} placeholder="Telefon" className="border rounded-lg px-3 py-1.5 text-sm" />
                                    <input type="text" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} placeholder="Şifre (min 6) *" className="border rounded-lg px-3 py-1.5 text-sm" />
                                  </div>
                                  <div className="flex gap-2">
                                    <button onClick={handleCreateUnitAccount} disabled={savingUser} className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold py-1.5 rounded-lg">
                                      {savingUser ? 'Oluşturuluyor...' : 'Hesabı Oluştur'}
                                    </button>
                                    <button onClick={() => setAccountUnit(null)} className="border text-gray-500 hover:bg-gray-50 text-sm px-3 py-1.5 rounded-lg">İptal</button>
                                  </div>
                                </div>
                              )}
                              {u.user_id && resetUserId === u.user_id && (
                                <div className="mt-2 flex gap-2 flex-wrap">
                                  <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Yeni şifre (min 6)" className="flex-1 min-w-[160px] border rounded-lg px-3 py-1.5 text-sm" />
                                  <button onClick={() => handleResetPassword(u.user_id)} className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-3 py-1.5 rounded-lg font-semibold">Kaydet</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Daireye bağlı olmayan sakin hesapları */}
                    {siteOverview.orphanResidents.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm text-gray-700 mb-2">⚠️ Daireye Bağlı Olmayan Hesaplar</h4>
                        <p className="text-xs text-gray-500 mb-2">Bu hesaplar bir daireyle eşleştirilmediği için sakin kendi faturasını göremez.</p>
                        {siteOverview.orphanResidents.map(o => (
                          <div key={o.id} className="border border-amber-200 bg-amber-50 rounded-xl p-3 mb-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-gray-800">{o.full_name}</p>
                                <p className="text-xs text-gray-500 truncate">{o.email}</p>
                              </div>
                              <button onClick={() => { setResetUserId(resetUserId === o.id ? null : o.id); setNewPassword(''); setResetMessage(null); }} className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap">🔑 Şifre</button>
                            </div>
                            {resetUserId === o.id && (
                              <div className="mt-2 flex gap-2 flex-wrap">
                                <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Yeni şifre (min 6)" className="flex-1 min-w-[160px] border rounded-lg px-3 py-1.5 text-sm" />
                                <button onClick={() => handleResetPassword(o.id)} className="bg-orange-600 hover:bg-orange-700 text-white text-sm px-3 py-1.5 rounded-lg font-semibold">Kaydet</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm py-6 text-center">Site bilgileri yüklenemedi.</p>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SuperAdminDashboard;